import type { RenderComponentProps } from "replo-runtime/shared/types";
import type { Component } from "schemas/component";

import React, { useState } from "react";

import { userFacingConsoleError } from "replo-runtime/shared/utils/userFacingConsole";

import { useBundleBuilder } from "../BundleBuilderContext";

const getCustomProps = (props: Component["props"]) => {
  const { addMoreToBundleText, addToCartText } = props;
  return {
    addMoreToBundleText: addMoreToBundleText as "string" | null,
    addToCartText: addToCartText as "string" | null,
  };
};
export const AddToCartButton: React.FC<RenderComponentProps> = ({
  componentAttributes,
  component,
}) => {
  const { addMoreToBundleText, addToCartText } = getCustomProps(
    component.props,
  );
  const { isSelectedThresholdMet, addBundleToCart } = useBundleBuilder();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isThresholdMet = isSelectedThresholdMet();

  function getButtonText() {
    if (isLoading) {
      return "Adding...";
    }

    if (isThresholdMet) {
      return addToCartText ?? "Add Bundle To Cart";
    }
    return addMoreToBundleText ?? "Select more items";
  }

  const handleClick = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await addBundleToCart();
    } catch (error) {
      setError(error instanceof Error ? error.message : "An error occurred");
      userFacingConsoleError("Error adding bundle to cart:", {
        error: error instanceof Error ? error.message : "An error occurred",
        message: error instanceof Error ? error.stack : "Unknown error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const styleOverrides = {
    cursor: !isThresholdMet ? "not-allowed" : "pointer",
    opacity: !isThresholdMet || isLoading ? 0.4 : undefined,
  };

  return (
    <div>
      <button
        {...componentAttributes}
        onClick={() => void handleClick()}
        disabled={!isThresholdMet || isLoading}
        style={{
          ...componentAttributes.style,
          ...styleOverrides,
        }}
      >
        {getButtonText()}
      </button>

      {error && (
        <div
          style={{
            color: "#f44336",
            marginTop: "10px",
            fontSize: "14px",
            textAlign: "center",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};
